import React from "react";
import { EyeIcon } from "@components/Common/SvgIcons/EyeIcon";
import { useTranslation } from "react-i18next";
import {
    DragDropContext,
    Droppable,
    Draggable,
    DropResult,
} from "react-beautiful-dnd";
import { ImageItem } from "./types";
import {
    Card,
    SectionTitle,
    ImageSection,
    ImageHeader,
    Select,
    AddButton,
    ResetAllButton,
    TableWrapper,
    Table,
    Th,
    Td,
    DragHandle,
    OrderInput,
    SmallToggle,
    ImageIcon,
    ActionsCell,
    ViewButton,
    DeleteButton,
} from "./styled";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";
import { ICON_SIZE_SMALL } from "../shared/constants";

type ImageTableProps = {
    mainColor: string;
    displayType: string;
    onDisplayTypeChange: (value: string) => void;
    currentImages: ImageItem[];
    uploadedImages: Record<number, string>;
    onDragEnd: (result: DropResult) => void;
    onOrderChange: (imageId: number, newOrder: string) => void;
    onToggleActive: (imageId: number) => void;
    onViewImage: (imageId: number) => void;
    onDeleteClick: (imageId: number) => void;
    onAddClick: () => void;
    onResetImages: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export function ImageTable({
    mainColor,
    displayType,
    onDisplayTypeChange,
    currentImages,
    uploadedImages,
    onDragEnd,
    onOrderChange,
    onToggleActive,
    onViewImage,
    onDeleteClick,
    onAddClick,
    onResetImages,
    fileInputRef,
    onImageUpload,
}: ImageTableProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-banner-image-section">
            <SectionTitle style={{ marginBottom: "15px" }}>
                {t("Banner image")}
            </SectionTitle>
            <ImageSection>
                <ImageHeader data-testid="scan-to-banner-image-header">
                    <Select
                        data-testid="scan-to-banner-display-type-select"
                        value={displayType}
                        onChange={(e) => onDisplayTypeChange(e.target.value)}
                    >
                        <option value="desktop">
                            Desktop 1920*550 (png, jpg, jpeg)
                        </option>
                        <option value="mobile">
                            Mobile 750*400 (png, jpg, jpeg)
                        </option>
                    </Select>
                    <input
                        data-testid="scan-to-banner-file-input"
                        ref={fileInputRef}
                        type="file"
                        accept="image/png, image/jpeg, image/jpg"
                        style={{ display: "none" }}
                        onChange={onImageUpload}
                    />
                    <AddButton
                        data-testid="scan-to-banner-add-image"
                        mainColor={mainColor}
                        onClick={onAddClick}
                    >
                        {t("Add")}
                    </AddButton>
                    <ResetAllButton
                        type="button"
                        data-testid="scan-to-banner-reset-images"
                        mainColor={mainColor}
                        onClick={onResetImages}
                        style={{ marginLeft: "auto" }}
                        title={t("Reset")}
                        aria-label={t("Reset")}
                    >
                        <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                    </ResetAllButton>
                </ImageHeader>

                <TableWrapper data-testid="scan-to-banner-table-wrapper">
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Table data-testid="scan-to-banner-table">
                            <thead>
                                <tr>
                                    <Th>{t("Order")}</Th>
                                    <Th>{t("Image")}</Th>
                                    <Th>{t("Activity")}</Th>
                                    <Th>{t("Image or video")}</Th>
                                    <Th style={{ textAlign: "center" }}>
                                        Actions
                                    </Th>
                                </tr>
                            </thead>
                            <Droppable droppableId="images">
                                {(provided) => (
                                    <tbody
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {currentImages.map((image, index) => (
                                            <Draggable
                                                key={image.id}
                                                draggableId={String(image.id)}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <tr
                                                        data-testid={`scan-to-banner-image-row-${image.id}`}
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        style={{
                                                            backgroundColor: image.active
                                                                ? `${mainColor}10`
                                                                : snapshot.isDragging
                                                                ? "#f8f9fa"
                                                                : "transparent",
                                                            ...provided
                                                                .draggableProps
                                                                .style,
                                                        }}
                                                    >
                                                        <Td>
                                                            <DragHandle
                                                                data-testid={`scan-to-banner-drag-handle-${image.id}`}
                                                                {...provided.dragHandleProps}
                                                            >
                                                                <svg
                                                                    width="16"
                                                                    height="16"
                                                                    viewBox="0 0 24 24"
                                                                    fill="currentColor"
                                                                >
                                                                    <path d="M3 15h18v-2H3v2zm0 4h18v-2H3v2zm0-8h18V9H3v2zm0-6v2h18V5H3z" />
                                                                </svg>
                                                                <OrderInput
                                                                    data-testid={`scan-to-banner-order-input-${image.id}`}
                                                                    type="number"
                                                                    value={
                                                                        image.order
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        onOrderChange(
                                                                            image.id,
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    min={1}
                                                                    max={
                                                                        currentImages.length
                                                                    }
                                                                    onClick={(
                                                                        e
                                                                    ) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                />
                                                            </DragHandle>
                                                        </Td>
                                                        <Td>
                                                            {uploadedImages[
                                                                image.id
                                                            ] ||
                                                            image.url ||
                                                            image.filename?.startsWith(
                                                                "http"
                                                            ) ? (
                                                                <img
                                                                    src={
                                                                        uploadedImages[
                                                                            image
                                                                                .id
                                                                        ] ||
                                                                        image.url ||
                                                                        image.filename
                                                                    }
                                                                    alt={
                                                                        image.filename
                                                                    }
                                                                    style={{
                                                                        width: 60,
                                                                        height: 40,
                                                                        objectFit:
                                                                            "cover",
                                                                        borderRadius: 4,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div
                                                                    style={{
                                                                        width: 60,
                                                                        height: 40,
                                                                        borderRadius: 4,
                                                                        background:
                                                                            "#eee",
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        justifyContent:
                                                                            "center",
                                                                    }}
                                                                >
                                                                    <svg
                                                                        width="20"
                                                                        height="20"
                                                                        viewBox="0 0 24 24"
                                                                        fill="#bbb"
                                                                    >
                                                                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </Td>
                                                        <Td>
                                                            <span
                                                                style={{
                                                                    fontWeight: image.active
                                                                        ? 600
                                                                        : 400,
                                                                    color: image.active
                                                                        ? mainColor
                                                                        : "#333",
                                                                }}
                                                            >
                                                                {t(
                                                                    image.activityLabel
                                                                )}
                                                            </span>
                                                        </Td>
                                                        <Td>
                                                            <div
                                                                style={{
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    gap: "10px",
                                                                }}
                                                            >
                                                                <ImageIcon>
                                                                    <svg
                                                                        width="16"
                                                                        height="16"
                                                                        viewBox="0 0 24 24"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                                                                    </svg>
                                                                </ImageIcon>
                                                                {image.filename
                                                                    .length > 10
                                                                    ? "..." +
                                                                      image.filename.slice(
                                                                          -10
                                                                      )
                                                                    : image.filename}
                                                            </div>
                                                        </Td>
                                                        <Td
                                                            style={{
                                                                textAlign:
                                                                    "center",
                                                            }}
                                                        >
                                                            <ActionsCell>
                                                                <SmallToggle
                                                                    data-testid={`scan-to-banner-toggle-active-${image.id}`}
                                                                    isActive={
                                                                        image.active
                                                                    }
                                                                    mainColor={
                                                                        mainColor
                                                                    }
                                                                    onClick={() =>
                                                                        onToggleActive(
                                                                            image.id
                                                                        )
                                                                    }
                                                                    aria-label={t(
                                                                        "Toggle image visibility"
                                                                    )}
                                                                />
                                                                <ViewButton
                                                                    data-testid={`scan-to-banner-view-image-${image.id}`}
                                                                    onClick={() =>
                                                                        onViewImage(
                                                                            image.id
                                                                        )
                                                                    }
                                                                    title={t(
                                                                        "View"
                                                                    )}
                                                                    aria-label={t(
                                                                        "View image"
                                                                    )}
                                                                >
                                                                    <EyeIcon />
                                                                </ViewButton>
                                                                <DeleteButton
                                                                    data-testid={`scan-to-banner-delete-image-${image.id}`}
                                                                    onClick={() =>
                                                                        onDeleteClick(
                                                                            image.id
                                                                        )
                                                                    }
                                                                    title="Supprimer l'image"
                                                                >
                                                                    <svg
                                                                        viewBox="0 0 24 24"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                                                    </svg>
                                                                </DeleteButton>
                                                            </ActionsCell>
                                                        </Td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                        </Table>
                    </DragDropContext>
                </TableWrapper>
            </ImageSection>
        </Card>
    );
}
